import { AxiosError } from 'axios'

import logger from '@/sdk/logger'
import { request, userRequest } from '@/sdk/oauth'
import { UserDetails } from '@/store/user/types'
import {
  PaymentMethodList,
  decodePaymentResponseOrThrow,
} from '@/store/paymentMethods/types/PaymentMethod'

export function getUserDetails(): Promise<UserDetails> {
  return userRequest.get('customer/me').then(response => response.data)
}

export function updateUserDetails(userDetails: UserDetails) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { customer_id, ...payload } = userDetails

  return userRequest.post('customer/me', payload).catch((error: AxiosError) => {
    const { response } = error

    if (response?.data?.reason === 'Email is already used') {
      throw new Error('EMAIL_EXISTS')
    }

    if (response?.data?.reason === 'INVALID_PHONE_NUMBER') {
      logger.error('failed normalising phone number', {
        error,
        tags: ['http', 'phone', 'user', 'validation'],
        additionalData: {
          customerId: customer_id,
          phoneNumber: payload.phone_number,
        },
      })

      throw new Error('INVALID_PHONE_NUMBER')
    }

    logger.error('failed updating user details', {
      error,
      tags: ['http', 'bookingFunnelSteps'],
    })

    throw error
  })
}

export function emailVerificationStatus(): Promise<{ is_verified: boolean }> {
  return new Promise((resolve, reject) => {
    userRequest
      .get('customer/email-verification-status')
      .then(response => resolve(response.data))
      .catch(error => reject(error))
  })
}

export function verifyEmailAddress(payload): Promise<void> {
  return new Promise((resolve, reject) => {
    request
      .post(`customer/verify/${payload}`)
      .then(response => resolve(response.data))
      .catch(error => reject(error))
  })
}

export function forgotPassword(email) {
  return request
    .post('customer/reset-password-request', { email })
    .then(() => true)
    .catch(() => false)
}

export function validateResetToken(token) {
  return request
    .post('customer/validate-reset-password-token', { token })
    .then(() => true)
    .catch(() => false)
}

export function resendEmailVerification() {
  return userRequest
    .post('customer/resend-verification-email')
    .then(() => true)
    .catch(() => false)
}

export function resetPassword(payload) {
  return request
    .post('customer/reset-password', payload)
    .then(response => response.data)
    .catch(() => false)
}

export function setPassword(password: { password: string }): Promise<void> {
  return userRequest
    .post('customer/me/password', password)
    .then(response => response.data)
    .catch((error: AxiosError) => {
      logger.error('failed changing the user password', {
        error,
        tags: [
          'registration',
          'http',
          'bookingFunnelSteps',
          'fakeEmail',
          'password',
        ],
      })

      throw error
    })
}

export function getIsPasswordSet(customerId: string): Promise<boolean> {
  return request
    .get(`customer/${customerId}/has-customer-set-password`)
    .then(response => response.data)
}

export interface AuthTokenResponse {
  access_token: string
  expires_in: number
  refresh_token: string
  token_type: string
}

export async function verifyOneTimeLoginTokenAndGetSession(
  token: string,
): Promise<AuthTokenResponse> {
  try {
    const { data } = await request.post<AuthTokenResponse>(
      `customer/one-time-login/${token}`,
    )

    return data
  } catch (error) {
    logger.error('failed using the one time login token', {
      error,
      tags: ['dashboard', 'client', 'oneTimeLogin'],
    })

    throw error
  }
}

interface CreateSepaPaymentMethodPayload {
  iban: string
  account_holder: {
    full_name: string
    street: string
    postal_code: string
    city: string
  }
  mandate_return_url: string
}

export async function createSepaPaymentMethod(
  payload: CreateSepaPaymentMethodPayload,
): Promise<void> {
  try {
    await userRequest.post('customer/me/payment-method/add-sepa', payload)
  } catch (error) {
    logger.error('failed adding sepa as payment method', {
      error,
      tags: ['dashboard', 'paymentMethods', 'sepa'],
    })

    throw error
  }
}

export async function fetchPaymentMethods(): Promise<PaymentMethodList> {
  try {
    const response = await userRequest.get<Record<string, unknown>[]>(
      'customer/me/payment-methods',
    )

    return decodePaymentResponseOrThrow(response.data)
  } catch (error) {
    logger.error('failed fetching payment methods', {
      error,
      tags: ['dashboard', 'paymentMethods'],
    })

    throw error
  }
}

export async function removePaymentMethod(
  paymentMethodId: string,
): Promise<void> {
  try {
    return await userRequest.post(
      `customer/me/payment-method/remove/${paymentMethodId}`,
    )
  } catch (error) {
    logger.error('failed removing payment method', {
      error,
      tags: ['dashboard', 'paymentMethods'],
      additionalData: {
        paymentMethodId,
      },
    })

    throw error
  }
}

export async function postAcceptTermsAndConditions(
  customerId: string,
): Promise<boolean> {
  return request.post(`customer/accept-terms-and-conditions/${customerId}`)
}
